import React from 'react'
import { Box, Button, Typography, makeStyles, Theme } from '@material-ui/core'

import {
    PipelineStepInstance,
    PipelineTransition,
} from './pipelineBuilder.types'
import { FlowConnectors } from './FlowConnectors'
import { FlowEdgeCard } from './FlowEdgeCard'
import { FlowNodeCard } from './FlowNodeCard'
import {
    benchHeight,
    benchWidth,
    nodeSlotWidth,
    transitionLayerTop,
    transitionLeft,
} from './flowLayout'

const useStyles = makeStyles((theme: Theme) => ({
    canvas: {
        flex: 1,
        minHeight: 280,
        overflow: 'auto',
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2, 1.5),
    },
    canvasFullWidth: {
        width: '100%',
    },
    bench: {
        position: 'relative',
        margin: 0,
    },
    nodeRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        position: 'relative',
        zIndex: 2,
    },
    nodeSlot: {
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'flex-start',
    },
    transitionLayer: {
        position: 'absolute',
        left: 0,
        right: 0,
        zIndex: 2,
    },
    empty: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(2),
        color: theme.palette.text.secondary,
        textAlign: 'center',
        padding: theme.spacing(4),
        minHeight: 280,
    },
    emptyTitle: {
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
}))

interface PipelineFlowChartProps {
    enabledSteps: PipelineStepInstance[]
    transitions: PipelineTransition[]
    editTarget:
        | { kind: 'node'; stepId: string }
        | { kind: 'edge'; fromStepId: string; toStepId: string }
        | null
    onNodeClick: (stepId: string) => void
    onEdgeClick: (fromStepId: string, toStepId: string) => void
    onEditSteps?: () => void
    readOnly?: boolean
    fullWidth?: boolean
}

export const PipelineFlowChart: React.FC<PipelineFlowChartProps> = ({
    enabledSteps,
    transitions,
    editTarget,
    onNodeClick,
    onEdgeClick,
    onEditSteps,
    readOnly = false,
    fullWidth = false,
}) => {
    const classes = useStyles()
    const stepCount = enabledSteps.length
    const layoutWidth = benchWidth(stepCount)
    const height = benchHeight()
    const canvasClassName = `${classes.canvas} ${
        fullWidth ? classes.canvasFullWidth : ''
    }`

    if (stepCount === 0) {
        return (
            <div className={canvasClassName}>
                <Box className={classes.empty}>
                    <Typography className={classes.emptyTitle}>
                        No pipeline steps yet
                    </Typography>
                    <Typography variant="body2">
                        Build a custom pipeline for this season using Edit
                        steps.
                    </Typography>
                    {!readOnly && onEditSteps && (
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={onEditSteps}
                        >
                            Edit steps
                        </Button>
                    )}
                </Box>
            </div>
        )
    }

    return (
        <div className={canvasClassName}>
            <Box
                className={classes.bench}
                style={{ width: layoutWidth, minHeight: height }}
            >
                <FlowConnectors transitionCount={transitions.length} />

                <Box className={classes.nodeRow} style={{ width: layoutWidth }}>
                    {enabledSteps.map((step, index) => (
                        <Box
                            key={step.id}
                            className={classes.nodeSlot}
                            style={{
                                width: nodeSlotWidth(index, stepCount),
                            }}
                        >
                            <FlowNodeCard
                                step={step}
                                selected={
                                    editTarget?.kind === 'node' &&
                                    editTarget.stepId === step.id
                                }
                                onClick={() => onNodeClick(step.id)}
                            />
                        </Box>
                    ))}
                </Box>

                <Box
                    className={classes.transitionLayer}
                    style={{
                        width: layoutWidth,
                        top: transitionLayerTop(),
                    }}
                >
                    {transitions.map((transition, index) => {
                        const fromStep = enabledSteps[index]
                        const toStep = enabledSteps[index + 1]
                        if (!fromStep || !toStep) {
                            return null
                        }
                        return (
                            <Box
                                key={transition.id}
                                style={{
                                    position: 'absolute',
                                    left: transitionLeft(index),
                                    top: 0,
                                }}
                            >
                                <FlowEdgeCard
                                    fromStep={fromStep}
                                    toStep={toStep}
                                    transition={transition}
                                    selected={
                                        editTarget?.kind === 'edge' &&
                                        editTarget.fromStepId === fromStep.id &&
                                        editTarget.toStepId === toStep.id
                                    }
                                    onClick={() =>
                                        onEdgeClick(fromStep.id, toStep.id)
                                    }
                                />
                            </Box>
                        )
                    })}
                </Box>
            </Box>
        </div>
    )
}
